import React, { useEffect, useState } from "react";
import { Button, IconButton, Item, Loading, Select, Text, TextInput, useNotifications } from "@ilc-technology/luik";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import IntlTelInput from "intl-tel-input/react";
import TBSection from "../../BaseComponents/TBSection";
import ErrorComponentWrapper from "../../ErrorHandling/ErrorComponentWrapper";
import { CustomerServiceApi } from "../../../apis/customerServiceApi";
import { useSessionContext } from "../../../contexts/SessionContext/SessionContext";
import { useDatasources } from "../../../contexts/StoryblokContext/StoryblokContext";
import { LabelKey } from "../../../Common/StoryblokTypes";
import { nameofFactory } from "../../../Common/Helpers/TextHelper";
import { convertToErrorDetails, getErrorDetailsToDisplay, logError } from "../../../Common/services/ErrorService";
import { ErrorCode, ErrorDetails } from "../../../Common/Types";
import {
  CreateRelatedContactRequest,
  CustomerRelationship,
  UpdateRelatedContactRequest,
} from "../../../apis/generatedCustomerServiceApiClient";
import InformationBox from "../../InformationBox/InformationBox";

type ParentDetailsForm = {
  contactId: string | null;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  contactRole: string;
  isEmergencyContact: boolean;
  isMainCommunicationContact?: boolean;
};

const EMPTY_FORM: ParentDetailsForm = {
  contactId: null,
  contactFirstName: "",
  contactLastName: "",
  contactEmail: "",
  contactPhone: "",
  contactRole: "",
  isEmergencyContact: false,
  isMainCommunicationContact: false,
};

const ParentDetails: React.FC = () => {
  const { labels } = useDatasources();
  const { session } = useSessionContext();
  const notifications = useNotifications();
  const customerServiceApi = new CustomerServiceApi();
  const nameofForm = nameofFactory<ParentDetailsForm>();

  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);
  const [loadingError, setLoadingError] = useState<ErrorDetails | undefined>(undefined);
  const [saveError, setSaveError] = useState<ErrorDetails | undefined>(undefined);
  const [initialValues, setInitialValues] = useState<ParentDetailsForm | undefined>(undefined);

  useEffect(() => {
    setIsLoading(true);
    setLoadingError(undefined);
    customerServiceApi
      .getRelatedContacts(session.accountUuid)
      .then((result) => {
        const mainContact =
          result.items?.find((rc) => rc.isMainCommunicationContact === true) ??
          result.items?.find((rc) => rc.isEmergencyContact === true) ??
          result.items?.[0];
        if (mainContact) {
          setInitialValues({
            contactId: mainContact.id ?? null,
            contactFirstName: mainContact.firstName ?? "",
            contactLastName: mainContact.lastName ?? "",
            contactEmail: mainContact.email ?? "",
            contactPhone: mainContact.phone ?? "",
            contactRole: mainContact.role ?? "",
            isEmergencyContact: mainContact.isEmergencyContact ?? false,
            isMainCommunicationContact: mainContact.isMainCommunicationContact ?? false,
          });
          setIsEditing(false);
        } else {
          setInitialValues(EMPTY_FORM);
          setIsEditing(true);
        }
      })
      .catch((error) => {
        const errorDetails = convertToErrorDetails(error, ErrorCode.RelatedContactFetchFailed);
        logError(errorDetails);
        setLoadingError(errorDetails);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleSubmit = async (values: ParentDetailsForm) => {
    setIsSaving(true);
    setSaveError(undefined);
    const payload: CreateRelatedContactRequest | UpdateRelatedContactRequest = {
      firstName: values.contactFirstName,
      lastName: values.contactLastName,
      email: values.contactEmail,
      phone: values.contactPhone,
      role: values.contactRole as CustomerRelationship,
      isEmergencyContact: values.isEmergencyContact,
      isMainCommunicationContact: values.isMainCommunicationContact,
    };
    try {
      if (values.contactId === null) {
        await customerServiceApi.addRelatedContact(session.accountUuid, payload as CreateRelatedContactRequest);
      } else {
        await customerServiceApi.updateRelatedContact(
          session.accountUuid,
          values.contactId,
          payload as UpdateRelatedContactRequest
        );
      }
      setInitialValues(values);
      setIsEditing(false);
      notifications.addSuccessNotification({ title: labels[LabelKey.studentDetailsSaved] });
    } catch (err: unknown) {
      const errorDetails = convertToErrorDetails(err, ErrorCode.RelatedContactCreationFailed);
      logError(errorDetails);
      setSaveError(errorDetails);
    } finally {
      setIsSaving(false);
    }
  };

  const validationSchema = Yup.object().shape({
    contactFirstName: Yup.string().max(40, labels[LabelKey.valueTooLong]).required(labels[LabelKey.validationMessage]),
    contactLastName: Yup.string().max(80, labels[LabelKey.valueTooLong]).required(labels[LabelKey.validationMessage]),
    contactEmail: Yup.string()
      .email(labels[LabelKey.validationEmailMessage])
      .max(80, labels[LabelKey.valueTooLong])
      .required(labels[LabelKey.validationMessage]),
    contactPhone: Yup.string().max(40, labels[LabelKey.valueTooLong]).required(labels[LabelKey.validationMessage]),
    contactRole: Yup.string().required(labels[LabelKey.validationMessage]),
  });

  const validation = (hasError: boolean): "invalid" | "valid" => (hasError ? "invalid" : "valid");

  const readOnlyLabels = [
    { label: labels[LabelKey.firstName], value: initialValues?.contactFirstName },
    { label: labels[LabelKey.lastName], value: initialValues?.contactLastName },
    { label: labels[LabelKey.email], value: initialValues?.contactEmail },
    { label: labels[LabelKey.phoneNumber], value: initialValues?.contactPhone },
    {
      label: labels[LabelKey.parentRelationship],
      value: labels["relationship_" + initialValues?.contactRole] ?? initialValues?.contactRole,
    },
  ];

  const renderReadOnly = () => (
    <div className="flex flex-col gap-1">
      {readOnlyLabels.map(({ label, value }) => (
        <div key={label} className="flex">
          <Text variant="paragraph-body-bold" className="mr-1">
            {label}:
          </Text>
          <Text variant="paragraph-body">{value}</Text>
        </div>
      ))}
    </div>
  );

  return (
    <ErrorComponentWrapper isLoading={isLoading} error={loadingError}>
      {!isLoading && !loadingError && initialValues && (
        <TBSection title={labels[LabelKey.parentDetails]}>
          <Text className="text-gray-45" variant="paragraph-body">
            {labels[LabelKey.parentDetailsDescription]}
          </Text>
          <div className="absolute right-0 top-0">
            <IconButton
              data-testid="edit-parent-details"
              iconName={isEditing ? "close" : "crayon"}
              intent={isEditing ? "secondary-black" : "primary-black"}
              size="xs"
              aria-label="edit-parent-details"
              onPress={() => {
                setIsEditing((prev) => !prev);
                setSaveError(undefined);
              }}
            />
          </div>

          {!isEditing && renderReadOnly()}

          {isEditing && (
            <Formik
              initialValues={initialValues}
              enableReinitialize
              validationSchema={validationSchema}
              validateOnMount
              validateOnBlur
              validateOnChange
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isValid }) => (
                <Form>
                  <div className="a-gap-sm flex flex-col">
                    <TextInput
                      data-testid="parent-firstName-input"
                      isRequired
                      name={nameofForm.contactFirstName}
                      label={labels[LabelKey.firstName]}
                      placeholder={labels[LabelKey.firstName]}
                      type="text"
                      value={values.contactFirstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      validationState={validation(!!(errors.contactFirstName && touched.contactFirstName))}
                      errorMessage={errors.contactFirstName}
                      touched={touched.contactFirstName}
                      isDisabled={isSaving}
                    />
                    <TextInput
                      data-testid="parent-lastName-input"
                      isRequired
                      name={nameofForm.contactLastName}
                      label={labels[LabelKey.lastName]}
                      placeholder={labels[LabelKey.lastName]}
                      type="text"
                      value={values.contactLastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      validationState={validation(!!(errors.contactLastName && touched.contactLastName))}
                      errorMessage={errors.contactLastName}
                      touched={touched.contactLastName}
                      isDisabled={isSaving}
                    />
                    <TextInput
                      data-testid="parent-email-input"
                      isRequired
                      name={nameofForm.contactEmail}
                      label={labels[LabelKey.email]}
                      placeholder={labels[LabelKey.email]}
                      type="email"
                      value={values.contactEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      validationState={validation(!!(errors.contactEmail && touched.contactEmail))}
                      errorMessage={errors.contactEmail}
                      touched={touched.contactEmail}
                      isDisabled={isSaving}
                    />
                    <div
                      className={`tel-container a-rounded flex flex-col justify-center border border-neutral-300
                        ${(errors.contactPhone && touched.contactPhone) || (touched.contactPhone && !isPhoneNumberValid) ? "tel-invalid" : ""}
                        ${isSaving ? "tel-container-disabled" : ""}`}
                    >
                      <IntlTelInput
                        data-testid="parent-phone-input"
                        initialValue={values.contactPhone}
                        onChangeValidity={(valid: boolean) => setIsPhoneNumberValid(valid)}
                        onChangeNumber={async (value) => await setFieldValue(nameofForm.contactPhone, value, true)}
                        usePreciseValidation
                        initOptions={{
                          utilsScript: "https://cdn.jsdelivr.net/npm/intl-tel-input@21.1.1/build/js/utils.js",
                          containerClass: "tel",
                          nationalMode: false,
                          strictMode: false,
                          formatAsYouType: true,
                          customPlaceholder: () => labels[LabelKey.phoneNumber],
                          initialCountry: "auto",
                          geoIpLookup: (success) => {
                            fetch("https://ipapi.co/json")
                              .then((res) => res.json())
                              .then((data) => success(data.country_code))
                              .catch(() => {});
                          },
                        }}
                        inputProps={{
                          onBlur: handleBlur(nameofForm.contactPhone),
                          name: "parent-phone-input",
                        }}
                      />
                    </div>
                    <Select
                      data-testid="parent-relationship-select"
                      name="parent-relationship-select"
                      label={labels[LabelKey.parentRelationship]}
                      className="mt-0"
                      defaultSelectedKey={values.contactRole}
                      isRequired
                      trackingInfo="track-select"
                      validationState={validation(!!(errors.contactRole && touched.contactRole))}
                      errorMessage={errors.contactRole}
                      onSelectionChange={async (value) => await setFieldValue(nameofForm.contactRole, value, true)}
                      disabled={isSaving}
                      touched={touched.contactRole}
                      onBlur={handleBlur}
                    >
                      {["Father", "Mother", "Guardian"].map((rel) => (
                        <Item key={rel} value={rel}>
                          {labels["relationship_" + rel] ?? rel}
                        </Item>
                      ))}
                    </Select>
                  </div>

                  {saveError && (
                    <div className="mt-4">
                      <InformationBox
                        testid="parent-save-error"
                        intent="alert"
                        title={labels[LabelKey.saveFailed]}
                        content={labels[LabelKey.saveFailedDescription]}
                        iconName="alert-circle-outlined"
                        additionalInfo={getErrorDetailsToDisplay(saveError)}
                      />
                    </div>
                  )}

                  <div className="float-right mt-6 flex flex-row items-center gap-4">
                    {initialValues.contactId !== null && (
                      <Button
                        data-testid="cancel-parent-details"
                        intent="secondary-black"
                        size="base"
                        isDisabled={isSaving}
                        onPress={() => {
                          setIsEditing(false);
                          setSaveError(undefined);
                        }}
                      >
                        {labels[LabelKey.cancel]}
                      </Button>
                    )}
                    <Button
                      data-testid="save-parent-details"
                      intent="primary-black"
                      size="base"
                      type="submit"
                      isDisabled={isSaving || !isValid || !isPhoneNumberValid}
                    >
                      {isSaving ? <Loading className="loading" size="sm" intent="white" /> : labels[LabelKey.save]}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </TBSection>
      )}
    </ErrorComponentWrapper>
  );
};

export default ParentDetails;
